/**
 * Keeping a tab honest about which organisation it is in.
 *
 * The selected organisation lives in one localStorage key, shared by every tab
 * on the browser, and the two halves of the app read it at different moments:
 *
 *   - `ErsAuthInterceptor` reads it **per request**, so every call carries
 *     whatever the newest selection is.
 *   - the chrome and the NgRx store read it **once**, when the tab loads.
 *
 * Switch organisation in one tab and the others keep the old name in the
 * header, the old menu and the old permissions, while their requests quietly
 * start carrying the new tenant. On DEV this produced a tab headed "Rectitude
 * Consulting Services" whose every read came back empty, because the requests
 * were scoped to CoreAxisPlaneTestOrg - and the organisation switcher refused
 * to help, because the tab believed it was already in Rectitude.
 *
 * Reading the wrong tenant's data is the visible half. The dangerous half is
 * writing: a form filled in while the header said one organisation would post
 * under another, which is the cross-tenant mistake this codebase has made more
 * often than any other.
 *
 * So a tab that learns the organisation changed elsewhere reloads. Blunt on
 * purpose: the tenant decides the menu, the role permissions and the scope of
 * every store in the app, and re-deriving all of that in place is a larger
 * promise than it looks. A reload is what the in-tab switch effectively does
 * already.
 */

/**
 * Who a stored tenant says you are, as one comparable string.
 *
 * Both halves matter. The same person can hold a different `userId` in each
 * organisation - that is what the switcher writes - so comparing the tenant id
 * alone would miss a switch between two organisations that happen to share one.
 *
 * Null when there is nothing stored or it cannot be parsed. An unreadable
 * value is treated as "no tenant" rather than as a new one, because the thing
 * it would otherwise trigger is a reload, and reloading on every unparseable
 * write is a loop.
 */
export function tenantIdentityOf(raw: string | null | undefined): string | null {
  if (!raw) return null;

  try {
    const tenant = JSON.parse(raw);
    const id = tenant?.id ?? null;

    if (!id) return null;

    return `${String(id).toLowerCase()}|${String(tenant?.userId ?? '').toLowerCase()}`;
  } catch {
    return null;
  }
}

/**
 * Whether a storage event is another tab changing the organisation.
 *
 * Deliberately narrow. The key is rewritten for reasons that are not a switch
 * at all - `setEmployeeIdForSTenant` merges an employee id into the same
 * object - and a tab that reloaded on every write to this key would reload
 * itself in the middle of somebody's work for no reason. Only a change of
 * identity counts.
 *
 * A removal does not count either. Signing out clears storage, and the sign-out
 * path already takes these tabs to the login page; reloading as well would race
 * it to the same place with a worse message.
 */
export function isTenantSwitch(event: {
  key: string | null;
  oldValue: string | null;
  newValue: string | null;
}, tenantKey: string): boolean {
  if (event.key !== tenantKey) return false;

  const after = tenantIdentityOf(event.newValue);
  if (after === null) return false;

  return tenantIdentityOf(event.oldValue) !== after;
}
